import React, {useEffect, useState} from 'react';
import moment from 'moment';
import _ from 'lodash';
import {useDatabase} from '@nozbe/watermelondb/react';
import Graph from 'src/modules/reports/components/graph';
import {StyleSheet, Text, View} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {Colors, Typography} from 'src/styles';
import {normalizeDay} from 'src/common-utils/normalizeDay';

const IntervalProgressGraph = ({
  sessions = [],
  program = {},
  envs = [],
  title = null,
  extras = {},
  startDate = '',
  endDate = '',
  byDay = false, // accepted but we don't pre-trim; bucketing handles it
  filteredEnvItems = [],
  shouldShowMenu = true,
}: any) => {
  const database = useDatabase();
  const [analysis, setAnalysis] = useState<any>({});

  // Build boundaries SAFELY (inclusive)
  const sb = normalizeDay(startDate);
  const eb = normalizeDay(endDate);
  const startBoundary = sb && sb.isValid() ? sb.clone().startOf('day') : null;
  const endBoundary = eb && eb.isValid() ? eb.clone().endOf('day') : null;
  const hasBounds = !!(startBoundary && endBoundary);

  const inRange = (ts: any) => {
    if (!hasBounds) {
      return true;
    }
    const m = normalizeDay(ts);
    if (!m || !m.isValid()) {
      return false;
    }
    return m.isSameOrAfter(startBoundary!) && m.isSameOrBefore(endBoundary!);
  };

  // Filter envs to the window
  const sortedEnvs = envs.filter((env: any) => inRange(env?.date));

  // Robust resolver for analysis entries (with fallback to session.average)
  const resolveAnalysis = (session: any, prog: any) => {
    const A = prog?.analysis || {};
    const dayKey = normalizeDay(
      session?.sessionDate ??
        session?.session_date ??
        session?.date ??
        session?.startTimestamp,
    )?.format('YYYY-MM-DD');

    const keysToTry = [
      session?.id,
      session?.sessionId,
      session?.session_id,
      session?.sessionDate,
      session?.session_date,
      dayKey,
    ].filter(Boolean);

    for (const k of keysToTry) {
      if (A[k]) {
        return {...A[k]};
      }
    }
    if (prog?.sessionId === session?.id) {
      return {...prog};
    }

    // Final fallback: use values already on the session
    if (session?.average != null) {
      return {
        average: Number(session.average) || 0,
        state: session?.state || '',
        collectors: session?.collectors || [],
      };
    }
    return null;
  };

  const calculateData = async () => {
    type Bucket = {
      points: Array<{
        average: number; // % responding this day
        state: string;
        collectors?: string[];
        startTimestamp: number;
      }>;
      change: Array<{date: number; title: string; description?: string}>;
      factor: Array<{date: number; title: string; description?: string}>;
    };

    const buckets: Record<string, Bucket> = {};
    const ensure = (key: string) => {
      if (!buckets[key]) {
        buckets[key] = {points: [], change: [], factor: []};
      }
      return buckets[key];
    };

    // ✅ Do NOT pre-trim for byDay; bucket/average by calendar day
    const analyzedSessions = sessions;

    // ---- Sessions → program points ----
    for (const session of analyzedSessions) {
      const m = normalizeDay(
        session?.sessionDate ??
          session?.session_date ??
          session?.date ??
          session?.startTimestamp,
      );
      if (!m || !m.isValid()) {
        continue;
      }

      const dayStart = m.clone().startOf('day');
      if (!inRange(dayStart.valueOf())) {
        continue;
      }

      const bucketKey = byDay
        ? dayStart.format('YYYY-MM-DD')
        : `${dayStart.format('YYYY-MM-DD')}_${
            session?.id || session?.startTimestamp
          }`;
      const analysisObj = resolveAnalysis(session, program);
      if (!analysisObj) {
        continue;
      }

      // Resolve collectors → names
      let collectors: string[] = [];
      if (
        Array.isArray(analysisObj?.collectors) &&
        analysisObj.collectors.length
      ) {
        collectors = await Promise.all(
          analysisObj.collectors.map(async (collectorId: string) => {
            if (collectorId !== 'System') {
              try {
                const u = await database.get('users').find(collectorId);
                return `${u?.firstName} ${u?.lastName}`;
              } catch {
                return 'System';
              }
            }
            return 'System';
          }),
        );
      }

      ensure(bucketKey).points.push({
        average: Number(analysisObj?.average) || 0, // Interval is %; Graph handles 0..100 axis
        state: analysisObj?.state || session?.state || '',
        collectors,
        startTimestamp: dayStart.valueOf(),
      });
    }

    // ---- Environmental items → bucket by day & type ----
    for (const env of sortedEnvs) {
      const m = normalizeDay(env?.date);
      if (!m || !m.isValid()) {
        continue;
      }

      const dayStart = m.clone().startOf('day');
      if (!inRange(dayStart.valueOf())) {
        continue;
      }

      const dayKey = dayStart.format('YYYY-MM-DD');
      const item = {
        date: dayStart.valueOf(),
        title: env?.title,
        description: env?.description,
      };

      if (env?.type === 'change') {
        ensure(dayKey).change.push(item);
      } else if (env?.type === 'factor') {
        ensure(dayKey).factor.push(item);
      } else {
        ensure(dayKey).factor.push(item);
      } // default bucket if type missing
    }

    // ---- Sort days chronologically ----
    const sortedKeys = Object.keys(buckets).sort(
      (a, b) =>
        moment(a, 'YYYY-MM-DD').valueOf() - moment(b, 'YYYY-MM-DD').valueOf(),
    );

    if (!sortedKeys.length) {
      setAnalysis({data: [], envLineData: [], phaseChanges: [], lines: []});
      return;
    }

    // ---- X-axis data: one entry per day ----
    const data = sortedKeys.map(key => ({
      startTimestamp: moment(key, 'YYYY-MM-DD').startOf('day').valueOf(),
      key,
    }));

    // ---- Daily program points (average across all sessions that day) ----
    const dailyPoints = sortedKeys
      .map((key, idx) => {
        const b = buckets[key];
        if (!b.points.length) {
          return null;
        }

        const avg =
          b.points.reduce((s, p) => s + (Number(p.average) || 0), 0) /
          b.points.length;

        const stateCounts = _.countBy(b.points.map(p => p.state || ''));
        const state =
          Object.keys(stateCounts).sort(
            // eslint-disable-next-line @typescript-eslint/no-shadow
            (a, b) => stateCounts[b] - stateCounts[a],
          )[0] || '';

        const collectors = _.uniq(b.points.flatMap(p => p.collectors || []));

        return {
          x: idx + 1,
          y: avg,
          date: moment(key, 'YYYY-MM-DD').startOf('day').valueOf(),
          collectors,
          state,
        };
      })
      .filter(Boolean) as Array<{
      x: number;
      y: number;
      date: number;
      collectors: string[];
      state: string;
    }>;

    // ---- Segment by state into `lines` (type: 'point') + phaseChanges ----
    const lines: any[] = [];
    const phaseChanges: any[] = [];

    if (dailyPoints.length) {
      let currentState = {
        state: dailyPoints[0].state,
        aggregate: dailyPoints[0].x,
        num: 1,
        index: dailyPoints[0].x,
      };
      let latestLine = {
        state: dailyPoints[0].state,
        points: [dailyPoints[0]],
        type: 'point' as const,
      };

      for (let i = 1; i < dailyPoints.length; i++) {
        const p = dailyPoints[i];
        if (p.state === latestLine.state) {
          latestLine.points.push(p);
          currentState = {
            ...currentState,
            aggregate: currentState.aggregate + p.x,
            num: currentState.num + 1,
          };
        } else {
          phaseChanges.push({
            line: currentState.index,
            x: currentState.aggregate / currentState.num,
            state: latestLine.state,
          });
          lines.push(latestLine);

          latestLine = {state: p.state, points: [p], type: 'point'};
          currentState = {
            state: p.state,
            aggregate: p.x,
            num: 1,
            index: p.x,
          };
        }
      }
      lines.push(latestLine);
      phaseChanges.push({
        line: currentState.index,
        x: currentState.aggregate / currentState.num,
        state: currentState.state,
      });
    }

    // ---- Unified environmental overlay per day ----
    sortedKeys.forEach((key, idx) => {
      const b = buckets[key];
      const x = idx + 1;
      const envItems = [
        ...b.change.map(item => ({...item, type: 'change'})),
        ...b.factor.map(item => ({...item, type: 'factor'})),
      ];
      if (envItems.length) {
        lines.push({
          x,
          y: 50,
          type: 'env',
          values: {
            [key]: envItems, // 'YYYY-MM-DD'
          },
        });
      }
    });

    setAnalysis({
      data,
      envLineData: [],
      phaseChanges,
      lines,
    });
  };

  useEffect(() => {
    calculateData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions, envs, program, startDate, endDate, byDay]);

  if (analysis?.data?.length > 0) {
    return (
      <Graph
        yAxisLabel={'% of Intervals'}
        data={analysis.data}
        program={program}
        envs={analysis.envLineData}
        phaseChanges={analysis.phaseChanges}
        lines={analysis.lines}
        title={title}
        extras={extras}
        filteredEnvItems={filteredEnvItems}
        shouldShowMenu={shouldShowMenu}
      />
    );
  }

  return (
    <View style={styles.emptyContainer}>
      <Icon name="chart-areaspline" size={30} color={'grey'} />
      <Text style={styles.emptyTitle}>No data</Text>
    </View>
  );
};

const styles = StyleSheet.create({
  emptyContainer: {
    flex: 1,
    minHeight: 100,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyTitle: {
    ...Typography.P3,
    color: Colors.TEXT_PRIMARY,
    marginTop: 8,
  },
});

export default React.memo(IntervalProgressGraph);
